/**
 * src/components/spatial/SpatialObjectChatCard.tsx
 *
 * Talking to a building, from the map.
 *
 * Until now the only way to hold a conversation with a spatial object
 * was the operator's simulator — an admin surface behind the presence
 * editor. So the hover card could truthfully say an object talks while
 * offering a visitor no way to make it do so, which is how "it says
 * TALKS and then says nothing" happened.
 *
 * No wallet is asked for. A question is not a transaction, and the
 * server rations anonymous visitors by their own persisted id rather
 * than demanding a signature to say hello. What a wallet buys is a
 * larger allowance, not admission.
 *
 * The chrome is `MapEntityTooltip` for the same reason the hover card
 * uses it: a visitor should not be able to tell that the conversation
 * came from a different part of the codebase than the card that
 * offered it.
 *
 * Speech is the point. The greeting arrives with the session and is
 * spoken immediately — the click that opened this card is the gesture
 * the browser's autoplay policy wants, which is exactly why the chat
 * opens from a click and not from a hover.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapEntityTooltip } from '../map/MapEntityTooltip';
import {
  endSpatialInteraction,
  findNearbyActions,
  findNearbyAgents,
  getValueFlow,
  sendSpatialMessage,
  speakSpatialAside,
  startSpatialInteraction,
  type NearbyActionsAnswer,
  type NearbyAgentsAnswer,
  type SpatialInteractionSession,
  type ValueFlowAnswer,
} from '../../services/spatial/spatialPresenceClient';
import { spatialParticipantId } from '../../services/spatial/spatialVisitorId';
import { ESCAPE_PRIORITY, useEscapeOwner } from '../../utils/escapeStack';
import ChatMessageBody from './ChatMessageBody';
import { useSpeechInput } from './useSpeechInput';
import { retryText } from './spatialBlockerText';
import {
  applyAside, applySend, canSend, seedChat, type ChatState, type ChatTurn,
} from './spatialChatModel';
import {
  resolveShortcut, shortcutsFor,
  type ChatShortcut, type ShortcutOrigin,
} from './chatShortcuts';
import { useUtterancePlayer } from './useUtterancePlayer';

export interface SpatialObjectChatCardProps {
  objectId: string;
  objectName: string;
  /** Matches the stripe on the hover card this was opened from. */
  accentColor?: string;
  /** Where on the map the conversation is anchored, in container pixels. */
  anchor: { x: number; y: number };
  viewport?: { width: number; height: number } | null;
  /**
   * Where the object itself is.
   *
   * Used for the shortcuts when the visitor has shared no location: an
   * answer about the building in front of them is useful, and claiming
   * it is an answer about where they are standing would not be. The
   * card says which one it got.
   */
  objectCenter?: { lng: number; lat: number } | null;
  /** Present only if the visitor happens to have connected one. */
  wallet?: string | null;
  location?: { latitude: number; longitude: number; accuracy?: number } | null;
  /**
   * Frame this object in 3D, if the map can.
   *
   * The existing `cinematicFlyTo` path, passed down rather than reimplemented.
   * Absent when there is no map to move, in which case the control is not
   * offered — a button that does nothing is worse than no button.
   */
  onCinematic?: (() => void) | null;
  onClose: () => void;
}

const CARD_WIDTH = 320;

const bubbleStyle = (who: ChatTurn['who']): React.CSSProperties => ({
  alignSelf: who === 'you' ? 'flex-end' : 'flex-start',
  maxWidth: '86%',
  padding: '6px 10px',
  borderRadius: 10,
  fontSize: 12,
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  // `pre-wrap` honours the newlines the answer builders write, and on its own
  // it will not break a run with no spaces in it. A contract address is such
  // a run, so a reply mentioning one made the card wider than the screen and
  // put a horizontal scrollbar under the conversation. `anywhere` lets a
  // token break; `minWidth: 0` stops a flex child from refusing to shrink,
  // which is the half that is easy to miss.
  overflowWrap: 'anywhere',
  minWidth: 0,
  background: who === 'you' ? 'rgba(99,102,241,0.28)' : 'rgba(255,255,255,0.07)',
  color: '#e2e8f0',
});

const noteStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#94a3b8',
  lineHeight: 1.4,
};

/**
 * One control in the row under the title.
 *
 * Small, quiet, and never covering the map: the whole point of a card
 * anchored to a building is that the building stays visible.
 */
const controlStyle = (active: boolean): React.CSSProperties => ({
  border: `1px solid ${active ? 'rgba(129,140,248,0.5)' : 'rgba(148,163,184,0.22)'}`,
  background: active ? 'rgba(99,102,241,0.22)' : 'rgba(255,255,255,0.04)',
  color: active ? '#c7d2fe' : '#94a3b8',
  borderRadius: 6,
  padding: '2px 6px',
  fontSize: 11,
  lineHeight: 1.3,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
});

/** A control that reports rather than toggles. Reads as flat, not clickable. */
const readoutStyle: React.CSSProperties = {
  ...controlStyle(false),
  cursor: 'default',
  border: '1px solid transparent',
  background: 'transparent',
};

export const SpatialObjectChatCard: React.FC<SpatialObjectChatCardProps> = ({
  objectId,
  objectName,
  accentColor = '#6366f1',
  anchor,
  viewport = null,
  objectCenter = null,
  wallet = null,
  location = null,
  onCinematic = null,
  onClose,
}) => {
  const [session, setSession] = useState<SpatialInteractionSession | null>(null);
  const [chat, setChat] = useState<ChatState | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(true);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { play, stop, speaking } = useUtterancePlayer(setRefusal);

  /**
   * Whether the visitor wants to hear this one.
   *
   * A local mute over the object's own setting, never the reverse: an object
   * with TTS switched off is silent regardless of what this says, because
   * that is the operator's decision and this is a preference. Audio already
   * in flight is stopped on mute, so the control does something immediately
   * rather than only affecting the next reply.
   */
  const [muted, setMuted] = useState(false);
  const objectSpeaks = session?.media?.ttsEnabled !== false;
  const speakingAllowed = objectSpeaks && !muted;

  /**
   * Play an utterance, unless the visitor has muted this conversation.
   *
   * Every playback goes through here rather than calling `play` directly, so
   * muting cannot be bypassed by a path somebody forgets to check. Held in a
   * ref-backed callback because the open effect below must not re-run when
   * the mute changes — re-running it would open a second session.
   */
  const allowedRef = useRef(speakingAllowed);
  allowedRef.current = speakingAllowed;
  const speak = useCallback((audioBase64: string, mime: string | null) => {
    if (!allowedRef.current) return;
    play(audioBase64, mime);
  }, [play]);

  const toggleMute = useCallback(() => {
    setMuted((was) => {
      // Silence now, not from the next reply onward.
      if (!was) stop();
      return !was;
    });
  }, [stop]);

  /**
   * The opening position, read at the moment the conversation opens.
   *
   * Held in refs rather than depended on, because they describe where
   * the visitor was when they tapped — not a reason to open a second
   * time. See the effect below for what depending on them cost.
   */
  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  const locationRef = useRef(location);
  locationRef.current = location;

  /**
   * Open the conversation, once.
   *
   * Guarded against React's double-invoked effects in development,
   * because opening twice would spend two of the visitor's rationed
   * turns and leave an orphan session behind on the server.
   *
   * Keyed on the object alone, and that is load-bearing. This used to
   * depend on the visitor's fix as well, which contradicted the guard
   * directly above: a new fix re-ran the effect, the guard returned
   * early, and the cleanup had already marked the open in flight as
   * abandoned — so the server seated a session whose greeting, turns
   * and end-of-spinner never reached the card. A visitor standing
   * still never saw it, because their fix stayed null and the prop
   * never changed identity. A visitor who went live sent one every
   * few seconds, against a greeting that takes several to synthesise,
   * and the chat hung every time.
   */
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    if (openedRef.current === objectId) return;
    openedRef.current = objectId;

    let abandoned = false;
    void (async () => {
      const at = locationRef.current;
      const result = await startSpatialInteraction(
        objectId,
        {
          type: 'human',
          id: spatialParticipantId(walletRef.current),
          wallet: walletRef.current ?? null,
          location: at
            ? {
                lat: at.latitude,
                lng: at.longitude,
                accuracyMeters: at.accuracy ?? null,
              }
            : null,
        },
        { greet: true },
      );
      if (abandoned) return;
      setBusy(false);

      if (!result.ok) {
        setRefusal(retryText(result));
        return;
      }

      const opened = (result as { session?: SpatialInteractionSession }).session;
      if (!opened?.sessionId) {
        setRefusal('The object opened no conversation.');
        return;
      }

      setSession(opened);
      setChat(seedChat(opened));
      if (opened.greetingSpeech?.audioBase64) {
        speak(opened.greetingSpeech.audioBase64, opened.greetingSpeech.mime);
      }
    })();

    return () => { abandoned = true; };
  }, [objectId, speak]);

  /**
   * What can be done around here, for the shortcut chips.
   *
   * One request when the card opens, in parallel with the greeting.
   * The visitor's own position when they have shared one, the object's
   * otherwise — and `origin` remembers which, because the difference
   * shows up in every sentence built from this.
   *
   * A failure is silent by design: shortcuts are a convenience, and a
   * red error over a conversation that is working fine would be a
   * worse card than one with no chips.
   */
  const [nearby, setNearby] = useState<NearbyActionsAnswer | null>(null);
  const [origin, setOrigin] = useState<ShortcutOrigin>('object');

  const fix = location && Number.isFinite(location.latitude) && Number.isFinite(location.longitude)
    ? { lat: location.latitude, lng: location.longitude, origin: 'visitor' as ShortcutOrigin }
    : (objectCenter && Number.isFinite(objectCenter.lat) && Number.isFinite(objectCenter.lng)
      ? { lat: objectCenter.lat, lng: objectCenter.lng, origin: 'object' as ShortcutOrigin }
      : null);
  const fixKey = fix ? `${fix.origin}:${fix.lat.toFixed(5)},${fix.lng.toFixed(5)}` : '';

  /**
   * The agents moving around here, and where a deposit would go.
   *
   * Held as `undefined` while unasked and `null` once a request failed, so
   * the answer builders can tell "not loaded" from "could not find out".
   * For agents that distinction is the whole point: reporting a failed
   * lookup as an empty list is how an object comes to deny that something
   * standing next to it exists.
   */
  const [agents, setAgents] = useState<NearbyAgentsAnswer | null | undefined>(undefined);
  const [valueFlow, setValueFlow] = useState<ValueFlowAnswer | null | undefined>(undefined);

  useEffect(() => {
    if (!fix) return undefined;
    let abandoned = false;
    setOrigin(fix.origin);
    void findNearbyActions({ lat: fix.lat, lng: fix.lng }).then((result) => {
      if (abandoned) return;
      setNearby(result.ok ? (result as unknown as NearbyActionsAnswer) : null);
    });
    void findNearbyAgents({ lat: fix.lat, lng: fix.lng }).then((result) => {
      if (abandoned) return;
      setAgents(result.ok ? (result as unknown as NearbyAgentsAnswer) : null);
    });
    return () => { abandoned = true; };
    // Keyed on the rounded position so a jittering fix does not
    // re-ask, and on the object so a second conversation re-asks.
  }, [objectId, fixKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Where a deposit here would go.
   *
   * Keyed on the wallet as well as the object: connecting one mid-conversation
   * is what turns "connect a wallet and I can tell you your balance" into a
   * balance, and re-asking is the only way that sentence stops being true.
   */
  useEffect(() => {
    let abandoned = false;
    void getValueFlow(objectId, { wallet }).then((result) => {
      if (abandoned) return;
      setValueFlow(result.ok ? (result as unknown as ValueFlowAnswer) : null);
    });
    return () => { abandoned = true; };
  }, [objectId, wallet]);

  /**
   * Answer a chip without asking the object.
   *
   * The answer already arrived from the same services the object's own
   * tool calls use, so this is a lookup rather than a conversation —
   * the text appears immediately and no model is asked for words we
   * are already holding. Nothing here executes: an action chip
   * explains, and the explanation ends by pointing at the panel where
   * a real execution would be authorized and signed.
   *
   * Then it is spoken, because the object has a voice everywhere else
   * and a chip answering in silence read as that voice breaking. The
   * text is shown before the audio is asked for and never waits on it:
   * synthesis is rationed and the browser may refuse to play, and in
   * both cases the answer is already on screen.
   */
  const answerShortcut = useCallback(async (shortcut: ChatShortcut) => {
    const resolved = resolveShortcut({
      shortcut,
      answer: nearby,
      agents: agents ?? null,
      valueFlow: valueFlow ?? null,
      objectName,
      origin,
    });

    const id = session?.sessionId;

    // A chip that needs the object's own words goes through the ordinary
    // message path rather than inventing a second one. That keeps one
    // conversation, one history and one ration — and it is why tapping "Read
    // about this" on an object with no cached description now produces a
    // description instead of the words on the button.
    if (resolved.kind === 'ask') {
      if (!id || !chat) return;
      setBusy(true);
      setRefusal(null);
      const result = await sendSpatialMessage(id, resolved.question, { speak: true });
      setBusy(false);
      setChat((prev) => (
        prev ? applySend(prev, resolved.question, result, new Date().toISOString()) : prev
      ));
      if (result.ok && result.speech?.audioBase64) {
        speak(result.speech.audioBase64, result.speech.mime);
      }
      return;
    }

    const answer = resolved.text;
    setChat((prev) => (
      prev ? applyAside(prev, shortcut.question, answer, new Date().toISOString()) : prev
    ));

    if (!id) return;

    const spoken = await speakSpatialAside(id, answer);
    if (spoken.ok && spoken.speech?.audioBase64) {
      speak(spoken.speech.audioBase64, spoken.speech.mime);
    }
  }, [nearby, agents, valueFlow, objectName, origin, session, chat, speak]);

  // Keep the newest turn in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat]);

  /**
   * Leaving hangs up.
   *
   * The session is released rather than left to expire so the visitor's
   * concurrent-session allowance is not consumed by conversations they
   * have already walked away from.
   */
  const close = useCallback(() => {
    stop();
    const id = session?.sessionId;
    if (id) void endSpatialInteraction(id, 'ended_user');
    onClose();
  }, [session, stop, onClose]);

  /**
   * Escape closes the conversation — and only the conversation.
   *
   * This used to add its own window listener, and so did the cinematic
   * teleport hook, so one keypress with a chat open over a cinematic ran
   * both: the conversation closed and the camera dropped out of cinematic at
   * the same time. A visitor who wanted to stop talking lost the view they
   * were talking about.
   *
   * Registered as a SURFACE, which outranks a MODE, so this runs and the
   * cinematic does not. A second Escape reaches the cinematic, because by
   * then this has deregistered. Nothing is suppressed: the registry picks one
   * of its own owners and unrelated global handlers still see the event.
   */
  useEscapeOwner(true, `object-chat-${objectId}`, ESCAPE_PRIORITY.SURFACE, close);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!session || !chat || !canSend(chat, text, busy)) return;

    setBusy(true);
    setRefusal(null);
    const result = await sendSpatialMessage(session.sessionId, text, { speak: true });
    setBusy(false);

    setChat((prev) => (prev ? applySend(prev, text, result, new Date().toISOString()) : prev));
    if (!result.ok) return;

    setDraft('');
    if (result.speech?.audioBase64) speak(result.speech.audioBase64, result.speech.mime);
  }, [draft, session, chat, busy, speak]);

  /**
   * Talking instead of typing.
   *
   * The transcript goes into the composer and stops there. It is not sent:
   * a final recognition result is words the visitor can see and correct, and
   * sending on their behalf turns a misheard place name into a message
   * nobody chose to say. They press Send, exactly as after typing.
   *
   * Interim results overwrite rather than append, because that is what the
   * recogniser means by interim — the same phrase, revised. What was already
   * typed is kept in front of it, so somebody who types half a question and
   * then speaks the rest gets both.
   */
  const typedBeforeListening = useRef('');
  const onTranscript = useCallback((text: string, isFinal: boolean) => {
    const prefix = typedBeforeListening.current;
    const joined = prefix ? `${prefix.replace(/\s+$/, '')} ${text}` : text;
    setDraft(joined);
    if (isFinal) {
      // The finished phrase becomes part of the fixed prefix, so a second
      // utterance in the same session adds to it instead of replacing it.
      typedBeforeListening.current = joined;
    }
  }, []);

  /**
   * Barge-in, decided by the object rather than by this component.
   *
   * `interruptMode` is the operator's setting, carried on the session from
   * GeoLink's presence config and used on the server to decide what a new
   * utterance does to one already playing. The same setting governs a visitor
   * talking over the object, so `queue` means let it finish and anything else
   * means the person wins. Reusing it is the point — a second, client-only
   * interruption policy would be a different answer to the same question.
   */
  const bargeIn = useCallback(() => {
    typedBeforeListening.current = draft;
    const mode = session?.media?.interruptMode || 'queue';
    if (mode === 'queue') return;
    if (speaking) stop();
  }, [draft, session, speaking, stop]);

  const mic = useSpeechInput({ onTranscript, onBeforeStart: bargeIn });

  const turns = chat?.turns ?? [];
  const remaining = chat?.remaining ?? 0;
  const closed = chat?.closed ?? false;
  // A recognition failure belongs in the same place as every other thing
  // that went wrong, and after them: a refused message matters more than a
  // microphone that heard nothing.
  const error = refusal || chat?.error || mic.error || null;
  const shortcuts = shortcutsFor({
    answer: nearby,
    // Offered once the lookup has settled either way: a chip that answers
    // "I could not tell" is more use than one that never appears, because
    // silence is what a visitor reads as "there is nothing here".
    agents: agents !== undefined,
    // Only when the object actually has a treasury to describe. An object
    // with no chain presence has nothing truthful to say about deposits.
    deposits: !!valueFlow,
  });

  return (
    <MapEntityTooltip
      data-testid="spatial-object-chat-card"
      anchor={anchor}
      viewport={viewport}
      // Scoped to the object, so two conversations are two surfaces.
      // Ranked above the hover cards: this was opened on purpose and a
      // pointer drifting across a country must not move it.
      surfaceId={`object-chat-${objectId}`}
      surfaceKind="object_chat"
      width={CARD_WIDTH}
      estimatedHeight={360}
      accentColor={accentColor}
      title={objectName}
      badge={(
        <button
          type="button"
          aria-label="Close conversation"
          onClick={close}
          style={{
            border: 'none', background: 'rgba(255,255,255,0.1)', color: '#94a3b8',
            borderRadius: 4, padding: '1px 6px', fontSize: 12, cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    >
      {/* Controls. Above the conversation so they do not move as it grows,
          and compact enough not to become the card. */}
      <div
        data-testid="spatial-chat-controls"
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4,
          padding: '6px 12px 0',
        }}
      >
        <button
          type="button"
          onClick={toggleMute}
          // Disabled rather than hidden when the object has no voice: the
          // absence is information, and the title says whose decision it was.
          disabled={!objectSpeaks}
          aria-pressed={speakingAllowed}
          data-testid="chat-control-voice"
          title={objectSpeaks
            ? (muted ? 'Voice off — tap to hear replies' : 'Voice on — tap to mute')
            : 'This object has no voice'}
          style={{
            ...controlStyle(speakingAllowed),
            opacity: objectSpeaks ? 1 : 0.45,
            cursor: objectSpeaks ? 'pointer' : 'not-allowed',
          }}
        >
          {speakingAllowed ? '🔊' : '🔇'}
          <span>{speaking ? 'Speaking' : 'Voice'}</span>
        </button>

        {mic.supported && (
          <button
            type="button"
            onClick={() => (mic.listening ? mic.stop() : mic.start())}
            disabled={closed || remaining <= 0}
            aria-pressed={mic.listening}
            data-testid="chat-control-mic"
            title={mic.listening
              ? 'Listening — tap to stop'
              : 'Speak your question. It goes into the box; you still press Send.'}
            style={controlStyle(mic.listening)}
          >
            {mic.listening ? '🔴' : '🎤'}
            <span>{mic.listening ? 'Listening' : 'Speak'}</span>
          </button>
        )}

        {onCinematic && (
          <button
            type="button"
            onClick={onCinematic}
            data-testid="chat-control-cinematic"
            // Says what it does to the map, because the chat staying open is
            // the part somebody would not assume.
            title="Frame this place in 3D. The conversation stays open."
            style={controlStyle(false)}
          >
            🎥
            <span>View</span>
          </button>
        )}

        {/* Whose "here" the answers are about. A readout, not a control:
            sharing a location is an application-level decision and lives in
            the panel that already owns it. Saying which one we used is what
            stops an answer about the building reading as an answer about
            where the visitor is standing. */}
        <span
          data-testid="chat-control-location"
          style={readoutStyle}
          title={origin === 'visitor'
            ? 'Answers are measured from your location'
            : 'You have not shared a location, so answers are measured from this place'}
        >
          {origin === 'visitor' ? '📍' : '📌'}
          <span>{origin === 'visitor' ? 'Your spot' : 'This place'}</span>
        </span>
      </div>

      <div
        ref={scrollRef}
        style={{
          display: 'flex', flexDirection: 'column', gap: 6,
          maxHeight: 220, overflowY: 'auto', padding: '10px 12px',
          // The container half of the overflow fix. Even if something inside
          // manages to exceed the width, the panel does not scroll sideways.
          overflowX: 'hidden',
        }}
      >
        {busy && turns.length === 0 && !error && (
          <div style={noteStyle}>Getting {objectName}’s attention…</div>
        )}
        {turns.map((t, i) => (
          <div key={`${t.at}-${i}`} style={bubbleStyle(t.who)}>
            <ChatMessageBody text={t.text} />
          </div>
        ))}
        {busy && turns.length > 0 && <div style={{ ...noteStyle, alignSelf: 'flex-start' }}>…</div>}
        {error && <div style={{ ...noteStyle, color: '#fca5a5' }}>{error}</div>}
      </div>

      {/* Shortcuts. Above the composer because they are the fast path
          and the composer is the slow one, and offered even before the
          nearby answer lands: the overview chip can truthfully say it
          could not find out. */}
      {session && !closed && shortcuts.length > 0 && (
        <div
          data-testid="spatial-chat-shortcuts"
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 5,
            padding: '0 12px 8px',
          }}
        >
          {shortcuts.map((shortcut) => (
            <button
              key={shortcut.id}
              type="button"
              onClick={() => { void answerShortcut(shortcut); }}
              // Said on the control itself, not only in the answer. A
              // visitor should know before tapping that nothing here
              // spends anything.
              title={shortcut.kind === 'overview'
                ? 'Ask what is available around here'
                : `What "${shortcut.label}" is — nothing is signed or spent`}
              style={{
                border: '1px solid rgba(148,163,184,0.25)',
                background: 'rgba(255,255,255,0.05)',
                color: '#cbd5e1',
                borderRadius: 999,
                padding: '3px 9px',
                fontSize: 10.5,
                fontWeight: 600,
                cursor: 'pointer',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {shortcut.label}
              {shortcut.requiresSigning && (
                <span style={{ color: '#fde68a', marginLeft: 4 }}>·</span>
              )}
            </button>
          ))}
        </div>
      )}

      {session && !closed && (
        <div style={{ padding: '0 12px 10px' }}>
          <textarea
            rows={2}
            value={draft}
            disabled={busy || remaining <= 0}
            placeholder={mic.listening
              ? 'Listening… the words appear here'
              : (remaining > 0 ? `Ask ${objectName} something…` : 'No questions left here.')}
            onChange={(e) => {
              setDraft(e.target.value);
              // An edit replaces what was dictated, so a following utterance
              // must build on what is now there rather than on the old
              // transcript.
              typedBeforeListening.current = e.target.value;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'none',
              background: 'rgba(255,255,255,0.06)', color: '#e2e8f0',
              border: `1px solid ${mic.listening ? 'rgba(129,140,248,0.55)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 8,
              padding: '6px 8px', fontSize: 12, fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={noteStyle}>
              {/* Said plainly, because a visitor who has just watched their
                  words appear would reasonably expect them to have been sent. */}
              {mic.listening ? 'Listening — press Send when ready' : `${remaining} left`}
            </span>
            <button
              type="button"
              disabled={!canSend(chat!, draft, busy)}
              onClick={() => void send()}
              style={{
                border: 'none', borderRadius: 8, padding: '5px 12px',
                fontSize: 11, fontWeight: 700,
                background: canSend(chat!, draft, busy) ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                color: canSend(chat!, draft, busy) ? '#818cf8' : '#475569',
                cursor: canSend(chat!, draft, busy) ? 'pointer' : 'not-allowed',
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </MapEntityTooltip>
  );
};

export default SpatialObjectChatCard;
